/**
 * Global Tabs Context
 *
 * React context for managing global tabs state and operations.
 *
 * @since 1.0.0
 */

import { __ } from '@wordpress/i18n';
import React, { createContext, useCallback, useContext, useState } from 'react';

import type { GlobalTab } from '../components/global-tabs/types';
import { useGlobalTabs, useNotifications } from '../hooks';
import { resetGlobalTabsToDefaults, saveGlobalTabs } from '../utils';
import type { GlobalTabsContextState, GlobalTabsProviderProps } from './types';

/**
 * Global tabs context
 *
 * @since 1.0.0
 */
const GlobalTabsContext = createContext<GlobalTabsContextState | undefined>(undefined);

/**
 * Global tabs context provider
 *
 * @since 1.0.0
 *
 * @param props Provider props.
 *
 * @return JSX element.
 */
export const GlobalTabsProvider: React.FC<GlobalTabsProviderProps> = ({ children }) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Get centralized notifications
  const { showSuccess: showGlobalSuccess, showError: showGlobalError } = useNotifications();

  // Notification handlers
  const handleError = useCallback(
    (message: string) => {
      showGlobalError(message);
    },
    [showGlobalError]
  );

  const handleSuccess = useCallback(
    (message: string) => {
      showGlobalSuccess(message);
    },
    [showGlobalSuccess]
  );

  // Use the data hook
  const {
    tabs,
    loading,
    error,
    hasUnsavedChanges,
    updateTabs,
    resetTabs,
    fetchTabs,
    fetchRedFoxTabs,
  } = useGlobalTabs({
    onError: handleError,
  });

  // Business operation: Save tabs
  const saveTabs = useCallback(async (): Promise<boolean> => {
    try {
      setIsSaving(true);
      const response = await saveGlobalTabs(tabs);
      handleSuccess(response.message);
      // Refresh data from API after successful save to sync originalTabs
      await fetchTabs();
      return true;
    } catch {
      const errorMessage = __(
        'Failed to save global tabs. Please try again.',
        'redfox-tab-manager'
      );
      handleError(errorMessage);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [tabs, handleSuccess, handleError, fetchTabs]);

  // Business operation: Delete tab (removes from context data only, save operation persists to DB)
  const deleteTab = useCallback(
    async (tabId: string): Promise<boolean> => {
      const tab = tabs.find((t: GlobalTab) => t.id === tabId);

      // Don't allow deletion of WooCommerce default tabs
      if (tab && tab.type === 'woocommerce_default') {
        handleError(__('WooCommerce default tabs cannot be deleted.', 'redfox-tab-manager'));
        return false;
      }

      try {
        setIsDeleting(true);

        // Remove the tab from local state only
        // The save operation will handle persisting changes to the database
        const updatedTabs = tabs.filter((tabItem: GlobalTab) => tabItem.id !== tabId);
        updateTabs(updatedTabs);

        return true;
      } catch {
        const errorMessage = __('Failed to remove tab. Please try again.', 'redfox-tab-manager');
        handleError(errorMessage);
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [tabs, handleError, updateTabs]
  );

  // Business operation: Reset to defaults
  const resetToDefaults = useCallback(async (): Promise<boolean> => {
    try {
      setIsSaving(true);
      const response = await resetGlobalTabsToDefaults();
      handleSuccess(response.message);
      // Refresh data from API after successful reset to sync state
      await fetchTabs();
      return true;
    } catch {
      const errorMessage = __(
        'Failed to reset global tabs to defaults. Please try again.',
        'redfox-tab-manager'
      );
      handleError(errorMessage);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [handleSuccess, handleError, fetchTabs]);

  const contextValue: GlobalTabsContextState = {
    // Data state
    tabs,
    loading,
    error,
    hasUnsavedChanges,

    // Operation states
    isSaving,
    isDeleting,

    // Data operations
    updateTabs,
    resetTabs,
    resetToDefaults,
    fetchTabs,
    fetchRedFoxTabs,

    // Business operations
    saveTabs,
    deleteTab,
  };

  return <GlobalTabsContext.Provider value={contextValue}>{children}</GlobalTabsContext.Provider>;
};

/**
 * Hook to use global tabs context
 *
 * @since 1.0.0
 *
 * @return Global tabs context state.
 */
export const useGlobalTabsContext = (): GlobalTabsContextState => {
  const context = useContext(GlobalTabsContext);
  if (context === undefined) {
    throw new Error('useGlobalTabsContext must be used within a GlobalTabsProvider');
  }
  return context;
};
